import { Component, Inject, OnDestroy } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { TEmbeddingExternalReference, TSourceDetail } from '@core/interfaces/TMessage';
import { TSourceItem } from '@core/interfaces/TSourceItem';

import { ApiConversationService } from '../../../../../api/services/agents/api-conversations.service';
import { TextHighlightService } from '../../../../infrastructure/helpers/text-highlight.service';
import { TAdaptedReferenceV3, TReferenceToDisplay } from '../../../../types/types';
import { GoToSourceService } from './go-to-source.service';
import { CommonService } from '@shared/services/common.service';
import { FoldersService } from '@shared/services/folders.service';
import { ReferenceDisplayPreferencesService } from '@shared/services/reference-display-preferences.service';

import { SourceContentDto } from '../../../../../api/dtos/conversation.dto';

export interface ResolvedReferenceDetail {
  sourceId: string;
  embeddingId?: string;
  detail: TSourceDetail;
}

export interface SourcePreviewModalData {
  selectedReference: TAdaptedReferenceV3;
  referenceForSelectList?: TReferenceToDisplay;
  sourceId: string;
  conversationId?: string;
  resolvedReferenceDetails?: ResolvedReferenceDetail[];
}

@Component({
  selector: 'app-source-preview-modal',
  templateUrl: './source-preview-modal.component.html',
  styleUrls: ['./source-preview-modal.component.scss'],
  standalone: true,
  imports: [MatButton, MatProgressSpinner],
})
export class SourcePreviewModalComponent implements OnDestroy {
  protected source: TSourceItem | undefined;
  protected sourceContent: SourceContentDto | undefined;
  protected isLoading = true;
  protected sourceNotFound = false;
  protected currentSelectedReference: TAdaptedReferenceV3 | null = null;
  protected currentHighlightText: string | undefined;
  private sub: Subscription | undefined;

  constructor(
    private _foldersService: FoldersService,
    private _apiConversationService: ApiConversationService,
    private dialogRef: MatDialogRef<SourcePreviewModalComponent>,
    private router: Router,
    private commonService: CommonService,
    private referenceDisplayPreferences: ReferenceDisplayPreferencesService,
    protected goToSourceService: GoToSourceService,
    @Inject(MAT_DIALOG_DATA)
    public data: SourcePreviewModalData
  ) {
    if (!this.data.sourceId) {
      this.isLoading = false;
      return;
    }
    this.loadSourceContent(this.data.sourceId);
  }

  private loadSourceContent(sourceId: string): void {
    if (!sourceId) {
      this.isLoading = false;
      return;
    }
    
    this.isLoading = true;

    // Use new lazy-load endpoint if conversationId is available
    if (this.data.conversationId) {
      this.sub?.unsubscribe();
      this.sub = this._apiConversationService
        .getSourceContent(this.data.conversationId, sourceId)
        .subscribe({
          next: (response: SourceContentDto) => {
            this.sourceContent = response;
            this.source = {
              _id: response.sourceId,
              name: response.sourceName,
              content: response.fullContent,
              folder: response.folderInfo ? [response.folderInfo.folderId] : [],
              fileLink: response.fileLink,
              // API may return metadata as an object (e.g. fingerprint); GoToSourceService expects an array
              metadata: Array.isArray(response.metadata) ? response.metadata : [],
            } as TSourceItem;

            this.isLoading = false;
            try {
              this.goToSourceService.setSource(this.source);
            } catch (e) {
              console.error('Failed to set source origin driver', e);
            }
            this.selectReference(this.data.selectedReference);
          },
          error: (err: any) => {
            if (err?.status === 404) {
              this.sourceNotFound = true;
              this.isLoading = false;
            } else {
              this.loadSourceContentFallback(sourceId);
            }
          }
        });
    } else {
      // Fallback: use old endpoint if no conversationId
      this.loadSourceContentFallback(sourceId);
    }
  }

  private loadSourceContentFallback(sourceId: string): void {
    this._foldersService.getSource(sourceId, res => {
      if (!res) {
        this.sourceNotFound = true;
        this.isLoading = false;
        return;
      }
      this.source = res;
      this.goToSourceService.setSource(res);
      this.isLoading = false;
      this.selectReference(this.data.selectedReference);
    }, () => {
      this.sourceNotFound = true;
      this.isLoading = false;
    });
  }

  protected selectReference(reference: TAdaptedReferenceV3) {
    const sourceDetail = this.resolveSourceDetail(reference);
    const selectedExternalReferenceIndex = this.normalizeSelectedExternalReferenceIndex(
      reference,
      sourceDetail
    );
    const selectedDisplayLabel =
      reference.uiSelectedDisplayLabel ??
      (selectedExternalReferenceIndex != null
        ? reference.uiDisplayLabels?.[selectedExternalReferenceIndex]
        : undefined) ??
      reference.uiDisplayLabel ??
      (reference.listPositionIndex != null ? String(reference.listPositionIndex) : undefined);

    this.currentSelectedReference = {
      ...reference,
      uiSelectedDisplayLabel: selectedDisplayLabel,
      uiSelectedExternalReferenceIndex: selectedExternalReferenceIndex,
    };

    if (reference.sourceId && reference.sourceId !== this.data.sourceId) {
      this.data.sourceId = reference.sourceId;
      this.loadSourceContent(reference.sourceId);
      return;
    }

    this.updateReferenceContext();
  }

  private getHighlightText(): string | undefined {
    if (this.currentSelectedReference?.uiTextToHighlight) {
      return this.currentSelectedReference.uiTextToHighlight;
    }

    const embeddingId = this.currentSelectedReference?.referenceDto?.embeddingId;
    if (embeddingId && this.sourceContent?.embeddings) {
      const embedding = this.sourceContent.embeddings.find(e => e.embeddingId === embeddingId);
      if (embedding?.content) {
        return embedding.content;
      }
    }

    return undefined;
  }

  protected selectedReferenceLabel(): string {
    if (this.currentSelectedReference?.uiSelectedDisplayLabel) {
      return this.currentSelectedReference.uiSelectedDisplayLabel;
    }
    if (this.currentSelectedReference?.uiDisplayLabel) {
      return this.currentSelectedReference.uiDisplayLabel;
    }
    return this.currentSelectedReference?.listPositionIndex != null
      ? String(this.currentSelectedReference.listPositionIndex)
      : '';
  }

  protected referencesForSelector(): TAdaptedReferenceV3[] {
    const children = this.data.referenceForSelectList?.children ?? [];
    if (!children.length) {
      return [];
    }

    const currentReference = this.currentSelectedReference ?? this.data.selectedReference;
    if (!this.hasReferenceInReferenceContent(currentReference)) {
      return children;
    }

    return children.filter(child => this.isSameReference(child, currentReference));
  }

  protected selectedReferenceTitle(): string {
    const selectedExternalReference = this.selectedExternalReference();
    if (selectedExternalReference?.referenceText) {
      return selectedExternalReference.referenceText;
    }

    return (
      this.resolveSourceDetail(this.currentSelectedReference)?.citation ||
      this.source?.name ||
      'Reference'
    );
  }

  protected sourceTitle(): string {
    return this.source?.name || this.resolveSourceDetail(this.currentSelectedReference)?.name || '';
  }

  protected selectedReferenceUrl(): string | undefined {
    const selectedExternalReference = this.selectedExternalReference();
    if (!selectedExternalReference) {
      return undefined;
    }
    return this.referenceDisplayPreferences.externalReferenceUrl(selectedExternalReference) || undefined;
  }

  protected selectedExternalReferences(): TEmbeddingExternalReference[] {
    return this.referenceDisplayPreferences.dedupeExternalReferences(
      this.resolveSourceDetail(this.currentSelectedReference)?.embeddingExternalReferences
    );
  }

  protected selectedExternalReference(): TEmbeddingExternalReference | undefined {
    const externalReferences = this.selectedExternalReferences();
    if (!externalReferences.length) {
      return undefined;
    }
    const requestedIndex = this.currentSelectedReference?.uiSelectedExternalReferenceIndex;
    if (requestedIndex == null) {
      return undefined;
    }
    return externalReferences[requestedIndex];
  }

  protected selectedExternalReferenceIndex(): number | null {
    if (!this.selectedExternalReferences().length) {
      return null;
    }
    return this.currentSelectedReference?.uiSelectedExternalReferenceIndex ?? null;
  }

  cancel() {
    this.dialogRef.close();
  }

  goToSource() {
    const folderId = this.source?.folder?.[0];
    const sourceId = this.source?._id;
    if (!folderId || !sourceId) {
      this.commonService.openSnackBar('No source details route found');
      return;
    }

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/sources/folder', folderId, 'source', sourceId])
    );
    window.open(url, '_blank');
  }

  goToOriginal() {
    const url = this.selectedReferenceUrl() || this.goToSourceService.getSourceUrl();
    if (url) {
      window.open(url, '_blank');
    } else {
      this.commonService.openSnackBar('No source link found');
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private updateReferenceContext(): void {
    this.currentHighlightText = this.getHighlightText();
    this.highlightText(this.currentHighlightText);
  }

  private resolveSourceDetail(reference?: TAdaptedReferenceV3 | null): TSourceDetail | undefined {
    if (!reference?.sourceId) {
      return this.data.referenceForSelectList?.originalSource;
    }

    const exactMatch = this.data.resolvedReferenceDetails?.find(
      item =>
        item.sourceId === reference.sourceId &&
        item.embeddingId === reference.referenceDto?.embeddingId
    );
    if (exactMatch) {
      return exactMatch.detail;
    }

    const sourceMatch = this.data.resolvedReferenceDetails?.find(item => item.sourceId === reference.sourceId);
    if (sourceMatch) {
      return sourceMatch.detail;
    }

    return this.data.referenceForSelectList?.originalSource;
  }

  private normalizeSelectedExternalReferenceIndex(
    reference: TAdaptedReferenceV3,
    sourceDetail: TSourceDetail | undefined
  ): number | null {
    const totalExternalReferences = sourceDetail?.embeddingExternalReferences?.length ?? 0;
    if (!totalExternalReferences) {
      return null;
    }

    const requestedIndex = reference.uiSelectedExternalReferenceIndex;
    if (
      typeof requestedIndex === 'number' &&
      requestedIndex >= 0 &&
      requestedIndex < totalExternalReferences
    ) {
      return requestedIndex;
    }

    const currentRequestedIndex = this.currentSelectedReference?.uiSelectedExternalReferenceIndex;
    if (
      this.isSameReference(reference, this.currentSelectedReference) &&
      typeof currentRequestedIndex === 'number' &&
      currentRequestedIndex >= 0 &&
      currentRequestedIndex < totalExternalReferences
    ) {
      return currentRequestedIndex;
    }

    return null;
  }

  private hasReferenceInReferenceContent(reference?: TAdaptedReferenceV3 | null): boolean {
    return (
      this.referenceDisplayPreferences.dedupeExternalReferences(
        this.resolveSourceDetail(reference)?.embeddingExternalReferences
      ).length > 0
    );
  }

  private isSameReference(left?: TAdaptedReferenceV3 | null, right?: TAdaptedReferenceV3 | null): boolean {
    if (!left || !right) {
      return false;
    }

    return (
      left.sourceId === right.sourceId &&
      left.referenceDto?.embeddingId === right.referenceDto?.embeddingId &&
      left.listPositionIndex === right.listPositionIndex
    );
  }

  private highlightText(markText?: string) {
    setTimeout(() => {
      if (markText) {
        TextHighlightService.mark('.mark-text', markText).then(marksTotal => {
          TextHighlightService.navigateToMarked('.marked-text-highlight', marksTotal);
        });
      } else {
        TextHighlightService.navigateToMarked('.marked-text-highlight');
      }
    }, 50);
  }

  log(name: string, value: any): void {
    console.log(name, value);
  }
}
